import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Box,
  Typography,
  IconButton
} from "@mui/material";
import { CloudUpload, Delete } from "@mui/icons-material";
import { Gallery } from "../types/gallery";
import { useAddGalleryMutation, useUpdateGalleryMutation } from "../api/galleryApi";

interface Props {
  open: boolean;
  setModalOpen: (open: boolean) => void;
  onSuccess?: () => void;
  gallery?: Gallery | null;
}

const AddEditGallery: React.FC<Props> = ({ open, setModalOpen, onSuccess, gallery }) => {
  const [formData, setFormData] = useState<{
    title: string;
    description: string;
    image: File | null;
    imagePreview: string;
  }>({
    title: "",
    description: "",
    image: null,
    imagePreview: "",
  });

  const [createGallery] = useAddGalleryMutation();
  const [updateGallery] = useUpdateGalleryMutation();
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (gallery) {
      setFormData({
        title: gallery.title,
        description: gallery.description,
        image: null,
        imagePreview: gallery.image,
      });
    } else {
      setFormData({
        title: "",
        description: "",
        image: null,
        imagePreview: "",
      });
    }
  }, [gallery, open]);

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setFormData({
        ...formData,
        image: file,
        imagePreview: URL.createObjectURL(file),
      });
    }
  };

  const handleRemoveImage = () => {
    setFormData({
      ...formData,
      image: null,
      imagePreview: "",
    });
  };

  const handleSubmit = async () => {
    const errors: { [key: string]: string } = {};
    if (!formData.title.trim()) {
      errors.title = "Title is required.";
    }
    if (!formData.description.trim()) {
      errors.description = "Description is required.";
    }
    if (!gallery && !formData.image) {
      errors.image = "Image is required for new gallery items.";
    }
    
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setSubmissionError(Object.values(errors)[0]);
      return;
    }

    setIsSubmitting(true);
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('description', formData.description);
      
      if (formData.image) {
        formDataToSend.append('image', formData.image);
      }

      if (gallery?.id) {
        await updateGallery({ id: gallery.id, body: formDataToSend }).unwrap();
      } else {
        await createGallery({ body: formDataToSend }).unwrap();
      }
      
      setModalOpen(false);
      onSuccess?.();
      setSubmissionError(null);
      setFieldErrors({});
    } catch (err) {
      setSubmissionError("Failed to save gallery item. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseError = () => {
    setSubmissionError(null);
  };

  return (
    <Dialog open={open} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>{gallery ? "Edit Gallery Item" : "Add Gallery Item"}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          margin="dense"
          label="Title"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          error={!!fieldErrors.title}
          helperText={fieldErrors.title}
        />
        
        <TextField
          fullWidth
          margin="dense"
          label="Description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          multiline
          rows={3}
          error={!!fieldErrors.description}
          helperText={fieldErrors.description}
        />

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Image
          </Typography>
          
          {formData.imagePreview && (
            <Box sx={{ mb: 2, position: 'relative', display: 'inline-block' }}>
              <img
                src={formData.imagePreview}
                alt="Preview"
                style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover' }}
              />
              <IconButton
                onClick={handleRemoveImage}
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  backgroundColor: 'error.main',
                  color: 'white',
                  '&:hover': { backgroundColor: 'error.dark' }
                }}
                size="small"
              >
                <Delete />
              </IconButton>
            </Box>
          )}

          <Button
            variant="outlined"
            component="label"
            startIcon={<CloudUpload />}
            sx={{ mt: 1 }}
          >
            Upload Image
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={handleImageChange}
            />
          </Button>
          
          {fieldErrors.image && (
            <Typography color="error" variant="caption" display="block" sx={{ mt: 1 }}>
              {fieldErrors.image}
            </Typography>
          )}
        </Box>

        {submissionError && (
          <Alert severity="error" onClose={handleCloseError} sx={{ mt: 2 }}>
            {submissionError}
          </Alert>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={() => setModalOpen(false)} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
        >
          {isSubmitting ? "Saving..." : (gallery ? "Update" : "Add")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditGallery; 